import React from 'react'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'

import { ArticleLinkedData } from 'components/linked-data/article'
import { TipsTemplate, AwardsBadgeContextProvider } from '../../../components'
import { buildStaticPaths, buildStaticProps } from '../../../ssg/tips-v2'
import { CompareCredit } from '../../../../types/compare-credit'
import { isSingleCardTip } from '../../../utils'
import { LayoutMainData } from 'ssg/utils/getLayoutMainData'

const Tip: React.FC<
  CompareCredit.TipV2 &
    LayoutMainData & {
      awards: Record<string, string>
    } & {
      redirectTo404: boolean
    }
> = (props) => {
  const router = useRouter()
  const { headline, seoDescription, seoTitle, redirectTo404, awards, labels } =
    props
  const awardsBadgeData = React.useMemo(
    () => ({
      awardsCardsSlugList: Object.keys(awards).length > 0 ? awards : undefined,
      tipLabels:
        labels && labels.length > 0
          ? labels.map(({ value }) => value)
          : undefined,
    }),
    [awards, labels],
  )

  React.useEffect(() => {
    if (redirectTo404) {
      router.replace('/404')
    }
  }, [router, redirectTo404])

  const [location, setLocation] = React.useState('')

  React.useEffect(() => setLocation(window.location.href), [props.slug])

  return !redirectTo404 ? (
    <AwardsBadgeContextProvider value={awardsBadgeData}>
      <NextSeo
        title={seoTitle || `${headline} | Compare Credit`}
        description={seoDescription || ''}
        canonical={location}
        openGraph={{
          url: location,
          images: [
            {
              url: props.image,
              alt: 'Og Image Alt',
            },
          ],
          title: seoTitle || `${headline} | Compare Credit`,
          description: seoDescription || '',
          site_name: 'Compare Credit',
          type: 'website',
        }}
        twitter={{
          handle: '@Compare__Credit',
          site: '@Compare__Credit',
          cardType: 'summary',
        }}
        facebook={{
          appId: '139213170647191',
        }}
      />
      <ArticleLinkedData tip={props} />
      <TipsTemplate
        {...(props as CompareCredit.TipV2 & LayoutMainData)}
        location={location}
      />
    </AwardsBadgeContextProvider>
  ) : (
    <></>
  )
}

export async function getStaticPaths() {
  return await buildStaticPaths('credit-cards')
}

export async function getStaticProps({
  params,
}: {
  params: {
    slug: string
  }
}): Promise<{
  props?: (CompareCredit.TipV2 & LayoutMainData) | { redirectTo404: boolean }
  revalidate?: number
}> {
  const tip = await buildStaticProps(params)

  if (tip?.props?.modules) {
    const { isHidden } = isSingleCardTip(tip?.props?.modules)
    if (isHidden) {
      return {
        props: { redirectTo404: true },
      }
    }
  }

  return {
    props: { ...tip.props, redirectTo404: false },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 120 seconds
    revalidate: 120, // 2 minutes
  }
}

export default Tip
